import React from 'react';
import { useState, useEffect } from 'react';
import { Table, Button, Select, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import PaginationComponent from '../../../components/pagination';
import contentReviewIcon from 'assets/images/icon-content-review.svg';
import {
	channelOptions,
	intentionOptions,
	contentReviewStatusOptions,
	contentReviewStatusStyles,
} from '../../../constants/';
import { useAutoSize } from 'hooks';
import { useSelector } from 'react-redux';
import * as Permissions from 'constants/permissions';
import * as api from 'api';
import { formatDateString, displayText, displayTextForArray } from 'utils';
import './index.css';

const { Option } = Select;

const ContentReview = () => {
	const navigate = useNavigate();

	const initialFilter = {
		intention: undefined,
		channel: undefined,
		status: undefined,
	};
	const [ref, tableHeight] = useAutoSize();

	const { permissions } = useSelector((state) => state.user);
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
		total: 0,
	});

	const [filter, setFilter] = useState(initialFilter);

	const [dataSource, setDataSource] = useState([]);

	useEffect(() => {
		// getContents({ page: 1, size: pagination.pageSize });
		getContents(requestParams({ pageSize: pagination.pageSize, current: 1 }, { ...initialFilter }));
	}, []);

	// MARK: - api calls
	function requestParams(pagination, filter) {
		return {
			page: pagination.current,
			size: pagination.pageSize,
			intention: filter.intention,
			status: filter.status,
			channel: filter.channel,
		};
	}

	async function getContents(params) {
		try {
			const response = await api.getChatList(params);
			setDataSource(response.list);
			setPagination((prev) => ({ ...prev, total: response.total || 0 }));
		} catch (error) {
			console.error('Fail to get review content list:', error);
		} finally {
		}
	}

	// MARK: - filter action handlers
	const onSearch = () => {
		setPagination((prev) => ({ ...prev, current: 1 }));
		getContents(requestParams({ pageSize: pagination.pageSize, current: 1 }, { ...filter }));
	};

	const onResetFilter = async () => {
		setFilter((prev) => ({ ...prev, ...initialFilter }));
		setPagination((prev) => ({ ...prev, current: 1 }));
		await getContents(requestParams({ pageSize: pagination.pageSize, current: 1 }, { ...initialFilter }));
	};

	// MARK: - table actions

	function onReviewButtonClicked(record) {
		navigate(`/guardrail/content-review/${record.sessionId}`, { state: record });
	}

	// MARK: - table data provider

	const _tableColumns = [
		{
			title: 'Channel',
			dataIndex: 'channel',
			width: 120,
			key: 'channel',
			ellipsis: {
				showTitle: false,
			},
			render: (channel) => {
				const channelOpt = channelOptions.filter((option) => option.value === channel);

				return (
					<div className="content-review-clm channel-clm">{channelOpt.length > 0 ? channelOpt[0]['label'] : '-'}</div>
				);
			},
		},
		{
			title: 'User ID',
			dataIndex: 'userId',
			key: 'userId',
			width: 240,
			render: (text) => <div className="content-review-clm userid-clm">{displayText(text)}</div>,
		},
		{
			title: 'User IP',
			dataIndex: 'ip',
			key: 'ip',
			width: 120,
			render: (text) => displayText(text),
		},
		{
			title: 'User Question',
			dataIndex: 'content',
			key: 'content',
			width: 360,
			ellipsis: {
				showTitle: false,
			},
			render: (text) => (
				<Tooltip placement="topLeft" title={text}>
					<div className="content-review-clm question-clm">{displayText(text)}</div>
				</Tooltip>
			),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			width: 120,
			render: (status) => {
				if (!status) return '-';
				return (
					<span
						style={{
							...contentReviewStatusStyles[status],
							textAlign: 'center',
							padding: '4px 0px',
							borderRadius: '4px',
							display: 'inline-block',
							width: '88px',
							height: '24px',
						}}
					>
						{contentReviewStatusOptions.filter((opt) => opt.value === status)[0]['label']}
					</span>
				);
			},
		},
		{
			title: 'Editor',
			dataIndex: 'reviewedBy',
			key: 'reviewedBy',
			width: 100,
			render: (text) => displayTextForArray(text),
		},
		{
			title: 'Creation Time',
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: 180,
			render: (text) => formatDateString(text),
		},
		{
			title: 'Update Time',
			dataIndex: 'reviewedAt',
			key: 'reviewedAt',
			width: 180,
			render: (text) => formatDateString(text),
		},
		{
			title: 'Action',
			key: 'action',
			fixed: 'right',
			width: 100,
			render: (_, record) => {
				return (
					<div className="action-btn-con">
						<div className="action-icon-size-all-con" onClick={() => onReviewButtonClicked(record)}>
							<img className="action-icon-size-all" src={contentReviewIcon} />
						</div>
					</div>
				);
			},
		},
	];

	const tableColumns = () => {
		// return columns;
		const clms = [..._tableColumns];
		if (!permissions.includes(Permissions.ContentReview_DETAIL)) {
			clms.pop();
		}
		return clms;
	};

	// MARK: - render

	return (
		<div className="app-page-container">
			<div className="app-navbar">
				<div className="app-navbar-text">Content Review</div>
			</div>
			<div className="app-filter-section">
				<div className="filter-items-row">
					<Select
						placeholder="Intention"
						value={filter.intention}
						onChange={(val) => setFilter({ ...filter, intention: val })}
						className="filter-item"
						allowClear
					>
						{intentionOptions.map((option) => (
							<Option key={option.value} value={option.value}>
								{option.label}
							</Option>
						))}
					</Select>
					<Select
						placeholder="Channel"
						value={filter.channel}
						onChange={(val) => setFilter({ ...filter, channel: val })}
						className="filter-item"
						allowClear
					>
						{channelOptions.map((option) => (
							<Option key={option.value} value={option.value}>
								{option.label}
							</Option>
						))}
					</Select>

					<Select
						placeholder="Status"
						value={filter.status}
						onChange={(val) => setFilter({ ...filter, status: val })}
						className="filter-item"
						allowClear
					>
						{contentReviewStatusOptions.map((option) => (
							<Option key={option.value} value={option.value}>
								{option.label}
							</Option>
						))}
					</Select>
				</div>
				<div className="filter-items-row">
					<div className="filter-actions">
						<Button color="purple" variant="filled" onClick={onSearch}>
							Search
						</Button>
						<Button variant="outlined" onClick={async () => await onResetFilter()}>
							Reset
						</Button>
					</div>
				</div>
			</div>

			<div className="app-table">
				<div className="app-table-content" ref={ref}>
					<Table
						rowKey={(record) => record.sessionId}
						dataSource={dataSource}
						columns={tableColumns()}
						pagination={false}
						scroll={{ y: tableHeight }}
					/>
				</div>
				<div className="table-line-all">
					<PaginationComponent
						pagination={pagination}
						onChange={(current, pageSize) => {
							setPagination({ ...pagination, current, pageSize });
							getContents(requestParams({ pageSize, current }, { ...filter }));
						}}
					/>
				</div>
			</div>
		</div>
	);
};

export default ContentReview;
